// components/mobile/MobileHome.jsx
// Mobile Home (replaces desktop Overview on narrow screens).
// App-style layout: gradient hero (menu, date, greeting, avatar) + status card,
// Quick Actions grid (favorites), Recently Visited, dashboards per category.
// Long-press a tile (~500ms) to toggle favorite.
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ShieldCheck, ShieldAlert, ShieldX, ChevronRight, ChevronDown, Clock, LayoutGrid, Star, Menu, Search, X,
} from 'lucide-react';
import { api, useAuth } from '../../context/AuthContext';
import { useLang } from '../../context/LanguageContext';
import { ROUTE_META } from '../../routesMeta';
import { BOARD_GROUPS, GROUP_KEY, groupOf } from '../../config/navGroups';
import { visualFor } from '../dashboardVisual';
import { useFavorites } from './favorites';

function canSee(route, hasAccess) {
  return route.permission === null ? true : hasAccess(route.permission);
}

function useLongPress(onLong, ms = 500) {
  const timer = useRef(null);
  const moved = useRef(false);
  const start = () => { moved.current = false; timer.current = setTimeout(() => { if (!moved.current) onLong(); }, ms); };
  const cancel = () => { if (timer.current) clearTimeout(timer.current); timer.current = null; };
  return {
    onTouchStart: start,
    onTouchMove: () => { moved.current = true; cancel(); },
    onTouchEnd: cancel,
    onContextMenu: (e) => e.preventDefault(),
  };
}

function initials(name) {
  const p = String(name || '?').trim().split(/\s+/);
  return ((p[0] || '')[0] || '?').toUpperCase() + ((p[1] || '')[0] || '').toUpperCase();
}

function fmtTime(s) {
  if (!s) return '';
  const raw = String(s).replace(' ', 'T'); const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(raw) ? raw : raw + 'Z');
  if (isNaN(d)) return '';
  return d.toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

const openDrawer = () => window.dispatchEvent(new Event('mobile-drawer-open'));

export default function MobileHome() {
  const nav = useNavigate();
  const { user, hasAccess } = useAuth();
  const { t } = useLang();
  const [health, setHealth] = useState(null);
  const [recent, setRecent] = useState([]);
  const [favs, toggleFav] = useFavorites();
  const [hint, setHint] = useState(null);
  const [q, setQ] = useState('');
  const [openGroups, setOpenGroups] = useState({});

  const flashHint = (label, added) => {
    setHint((added ? 'Added to Quick Actions' : 'Removed from Quick Actions') + ': ' + label);
    setTimeout(() => setHint(null), 1500);
  };

  useEffect(() => {
    let alive = true;
    api.get('/health/deep')
      .then(({ data }) => { if (alive) setHealth(data); })
      .catch(() => { if (alive) setHealth(false); });
    return () => { alive = false; };
  }, []);

  const shortcuts = ROUTE_META.filter(r => r.key !== 'overview' && canSee(r, hasAccess));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/logs', {
          params: { action: 'VIEW_DASHBOARD', username: user?.username, limit: 40 },
        });
        if (cancelled) return;
        const logs = data?.logs || [];
        const seen = new Set();
        const out = [];
        for (const log of logs) {
          const label = String(log.detail || '').replace(/^Membuka:?\s*/i, '').trim();
          if (!label) continue;
          const route = shortcuts.find(r => r.label.toLowerCase() === label.toLowerCase());
          if (!route || seen.has(route.key)) continue;
          seen.add(route.key);
          out.push({ ...route, at: log.created_at });
          if (out.length >= 6) break;
        }
        setRecent(out);
      } catch { if (!cancelled) setRecent([]); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [user]);

  const name = user?.fullName || user?.username || 'User';
  const first = String(name).split(/\s+/)[0];
  const today = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'short' });

  const favRoutes = favs.map(k => shortcuts.find(r => r.key === k)).filter(Boolean);
  const quick = favRoutes.length ? favRoutes : shortcuts.slice(0, 8);

  const ql = q.trim().toLowerCase();
  const filtered = ql ? shortcuts.filter(r => r.label.toLowerCase().includes(ql)) : shortcuts;
  const groups = BOARD_GROUPS
    .map(g => ({ g, label: t(GROUP_KEY[g]) || g, items: filtered.filter(r => groupOf(r.key) === g) }))
    .filter(x => x.items.length);
  const grouped = new Set(groups.flatMap(x => x.items.map(r => r.key)));
  const others = filtered.filter(r => !grouped.has(r.key));
  if (others.length) groups.push({ g: '__other', label: 'Other', items: others });

  const onLongTile = (r) => { const added = !favs.includes(r.key); toggleFav(r.key); flashHint(r.label, added); };

  return (
    <div className="min-h-full bg-[#f6f3fb]" style={{ paddingBottom: '16px' }}>
      {/* Hero */}
      <div className="relative rounded-b-[28px] px-4 pb-4 text-white shadow-lg"
        style={{ paddingTop: 'calc(14px + env(safe-area-inset-top))', background: 'linear-gradient(160deg,#2e0854 0%,#4c1287 55%,#6d28d9 100%)' }}>
        <div className="flex items-center gap-3">
          <button onClick={openDrawer} aria-label="Menu"
            className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center active:bg-white/25">
            <Menu style={{ width: 18, height: 18 }} />
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] text-white/70 leading-tight">{today}</p>
            <h1 className="text-[20px] font-bold leading-tight truncate">Hi, {first}</h1>
          </div>
          <button onClick={openDrawer} aria-label="Profile"
            className="w-10 h-10 rounded-full bg-white/20 ring-2 ring-white/30 flex items-center justify-center text-sm font-bold">
            {initials(name)}
          </button>
        </div>

        <HealthCard health={health} lastVisit={recent[0]}
          onOpen={hasAccess('logs') ? () => nav('/dashboard/logs') : null} />
      </div>

      <div className="px-4">
        <SectionTitle>{favRoutes.length ? 'Quick Actions' : 'Quick Actions · long-press to pin'}</SectionTitle>
        <div className="grid grid-cols-4 gap-2">
          {quick.map(r => (
            <QuickTile key={r.key} route={r} onClick={() => nav(r.path)} onLong={() => onLongTile(r)} />
          ))}
        </div>

        {hint && <p className="mt-2 px-3 py-2 rounded-xl bg-violet-100 text-violet-800 text-xs">{hint}</p>}

        {recent.length > 0 && (
          <>
            <SectionTitle icon={Clock}>Recently Visited</SectionTitle>
            <div className="flex gap-2.5 overflow-x-auto -mx-4 px-4 pb-1" style={{ scrollbarWidth: 'none' }}>
              {recent.map(r => {
                const v = visualFor(r.key);
                const Icon = v.icon || LayoutGrid;
                return (
                  <button key={r.key} onClick={() => nav(r.path)}
                    className="flex items-center gap-2.5 shrink-0 pl-2.5 pr-3 py-2.5 rounded-2xl bg-white shadow-sm active:bg-slate-50 text-left">
                    <span className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center">
                      <Icon className="text-violet-700" style={{ width: 15, height: 15 }} />
                    </span>
                    <span className="flex flex-col">
                      <span className="text-[12.5px] font-semibold text-slate-700 whitespace-nowrap">{r.label}</span>
                      <span className="text-[10.5px] text-slate-400">{fmtTime(r.at)}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </>
        )}

        <SectionTitle>All Dashboards</SectionTitle>
        <div className="flex items-center gap-2 px-3 h-10 rounded-2xl bg-white shadow-sm mb-3">
          <Search className="text-slate-400" style={{ width: 16, height: 16 }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search dashboards"
            className="flex-1 bg-transparent outline-none text-[13px] text-slate-700 placeholder:text-slate-400" />
          {q && <button onClick={() => setQ('')} aria-label="Clear"><X className="text-slate-400" style={{ width: 15, height: 15 }} /></button>}
        </div>

        <div className="space-y-2.5">
          {groups.map(({ g, label, items }) => {
            const open = ql ? true : !!openGroups[g];
            return (
              <div key={g} className="rounded-2xl bg-white shadow-sm overflow-hidden">
                <button onClick={() => setOpenGroups(s => ({ ...s, [g]: !s[g] }))}
                  className="w-full flex items-center justify-between px-4 py-3 active:bg-slate-50">
                  <span className="text-[13px] font-semibold text-slate-700">{label}</span>
                  <span className="flex items-center gap-2">
                    <span className="text-[11px] font-semibold text-violet-700 bg-violet-100 rounded-full px-2 py-0.5">{items.length}</span>
                    <ChevronDown className={'text-slate-400 transition-transform ' + (open ? 'rotate-180' : '')} style={{ width: 16, height: 16 }} />
                  </span>
                </button>
                {open && (
                  <div className="border-t border-slate-100">
                    {items.map(r => (
                      <ListRow key={r.key} route={r} starred={favs.includes(r.key)}
                        onClick={() => nav(r.path)} onLong={() => onLongTile(r)} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
          {!groups.length && <p className="text-center text-xs text-slate-400 py-6">No dashboard matches "{q}"</p>}
        </div>
      </div>
    </div>
  );
}

function SectionTitle({ children, icon: Icon }) {
  return (
    <p className="mt-5 mb-2 px-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
      {Icon && <Icon style={{ width: 12, height: 12 }} />} {children}
    </p>
  );
}

function QuickTile({ route, onClick, onLong }) {
  const v = visualFor(route.key);
  const Icon = v.icon || LayoutGrid;
  const lp = useLongPress(onLong);
  return (
    <button onClick={onClick} {...lp}
      className="flex flex-col items-center justify-start gap-1.5 pt-3 pb-2 px-1 rounded-2xl bg-white shadow-sm active:bg-slate-50 select-none min-h-[82px]"
      style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
      <span className={'w-9 h-9 rounded-xl flex items-center justify-center ' + (v.bgColor || 'bg-slate-100')}>
        <Icon className={v.color} style={{ width: 18, height: 18 }} />
      </span>
      <span className="text-[10.5px] font-medium text-slate-700 leading-tight text-center line-clamp-2">{route.label}</span>
    </button>
  );
}

function ListRow({ route, starred, onClick, onLong }) {
  const v = visualFor(route.key);
  const Icon = v.icon || LayoutGrid;
  const lp = useLongPress(onLong);
  return (
    <button onClick={onClick} {...lp}
      className="w-full flex items-center gap-3 px-4 py-2.5 active:bg-slate-50 text-left select-none"
      style={{ WebkitUserSelect: 'none', WebkitTouchCallout: 'none' }}>
      <span className={'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ' + (v.bgColor || 'bg-slate-100')}>
        <Icon className={v.color} style={{ width: 16, height: 16 }} />
      </span>
      <span className="flex-1 text-[13px] text-slate-700">{route.label}</span>
      {starred && <Star className="text-amber-400 fill-amber-400" style={{ width: 13, height: 13 }} />}
      <ChevronRight className="text-slate-300" style={{ width: 15, height: 15 }} />
    </button>
  );
}

function HealthCard({ health, lastVisit, onOpen }) {
  let Icon = ShieldCheck, title = 'Checking status…', sub = '', pill = null;
  if (health === false) { Icon = ShieldAlert; title = 'Status unavailable'; sub = 'Cannot load /health/deep'; }
  else if (health) {
    const s = health.status;
    if (s === 'ok') { Icon = ShieldCheck; title = 'All systems normal'; pill = ['Normal', 'bg-emerald-400/90 text-emerald-950']; }
    else if (s === 'down') { Icon = ShieldX; title = 'Some systems down'; pill = ['Down', 'bg-rose-400 text-rose-950']; }
    else { Icon = ShieldAlert; title = 'Partially degraded'; pill = ['Degraded', 'bg-amber-300 text-amber-950']; }
    const c = health.checks || {};
    const cron = c.cron?.jobs ? c.cron.jobs.filter(j => j.healthy).length + '/' + c.cron.jobs.length + ' cron' : null;
    const db = c.db?.ok === false ? 'DB error' : 'DB ok';
    const ai = c.aiService ? (c.aiService.ok ? 'AI ok' : 'AI down') : null;
    sub = [db, cron, ai].filter(Boolean).join(' · ');
  }
  return (
    <button onClick={onOpen || undefined} disabled={!onOpen}
      className="mt-4 w-full rounded-2xl bg-white/10 backdrop-blur border border-white/15 px-4 py-3 text-left active:bg-white/15">
      <div className="flex items-center gap-2.5">
        <Icon style={{ width: 18, height: 18 }} className="shrink-0 text-white/90" />
        <p className="flex-1 text-[13px] font-semibold">{title}</p>
        {onOpen && <ChevronRight style={{ width: 15, height: 15 }} className="text-white/60" />}
      </div>
      {sub && <p className="mt-1 text-[11px] text-white/70 truncate">{sub}</p>}
      <div className="mt-3 pt-2.5 border-t border-white/15 flex items-center gap-2">
        <Clock style={{ width: 12, height: 12 }} className="text-white/60" />
        <p className="flex-1 text-[11px] text-white/75 truncate">
          {lastVisit ? 'Last opened: ' + lastVisit.label + ' · ' + fmtTime(lastVisit.at) : 'No recent activity'}
        </p>
        {pill && <span className={'text-[10.5px] font-bold rounded-full px-2.5 py-0.5 ' + pill[1]}>{pill[0]}</span>}
      </div>
    </button>
  );
}
